import { useState, useEffect } from 'react'
import { api, formatDate, isFutureOrToday } from '../api'
import { useAuth } from '../AuthContext'
import RinaContextCard from '../components/RinaContextCard'
import { trierTournees, tacheDe, fmtDate } from '../components/paniers/prod/paniersProd'
import { useTheme } from '../theme'
import { useActionsPortail } from '../contexts/navigation'
import PageHub from '../components/PageHub'
import CarteMaPage from '../components/producteur/CarteMaPage'
import CarteDossierAccueil from '../components/producteur/CarteDossierAccueil'
import MarcheProducerEvents from '../components/marche/MarcheProducerEvents'
import { MVP } from '../mvp'
import { avecAlpha } from '../lib/couleurs'

/**
 * Accueil producteur — fidele au prototype v7.2 (tab accueil, lentille producteur) :
 * hero glassDark avec barre d'accent module + halo, carte « Votre activite · {mois} »
 * (CA serif + mini-graphe → ouvre l'ecran Activite), carte contextuelle Marche,
 * feed « En ce moment » (tuile emoji + chip module + bord gauche colore).
 * v7.3 : le hero devient la carte « Prochaine tache » (tournee la plus proche
 * ou une action est attendue) → ouvre le module Paniers producteur ; le hero
 * marche/offres reste en secours quand aucune tournee n'est active.
 */

// Couleurs des modules (prototype v7.2, MODULE_THEME)
const MOD = {
  marche: { c: 'rgb(var(--c-teal))', e: '🎪', l: 'Évènements' }, // ADR-062 : nom du LIEU
  paniers: { c: 'rgb(var(--c-primary))', e: '🧺', l: 'Paniers' },
  offres: { c: '#6b5b95', e: '📦', l: 'Mes offres' },
}

const ModuleChip = ({ m }) => (
  <span className="inline-flex items-center gap-1 text-[9.5px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full"
    style={{ color: m.c, background: avecAlpha(m.c, 0.12), border: `1px solid ${avecAlpha(m.c, 0.25)}` }}>
    {m.e} {m.l}
  </span>
)

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
const eur = (n) => Number(n || 0).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

// Titre du hero « Prochaine tache » selon l'etape de la tournee (proto v7.3)
const TITRE_TACHE = {
  DISPONIBILITES: 'Proposez vos produits',
  COMMANDES: 'Ça se vend',
  PREPARATION: 'Préparez votre collecte',
  LIVRAISON: 'Manrina livre',
  TERMINE: 'Votre argent arrive',
}

function ContenuAccueilProducteur() {
  const { ouvrirMarcheProducteur, ouvrirPaniersProducteur, setHubTab: onTabChange } = useActionsPortail()
  const onOpenMarches = () => ouvrirMarcheProducteur(null)
  const onOpenOffers = () => onTabChange('offers')
  const onOpenPaniersProd = ouvrirPaniersProducteur
  const { isWeb3 } = useTheme()
  const { token } = useAuth()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const cardBg = 'panneau-verre'

  const [marches, setMarches] = useState([])
  const [history, setHistory] = useState([])
  const [tours, setTours] = useState([])
  const [loading, setLoading] = useState(true)
  const [flash, setFlash] = useState(null)
  const toast = (msg) => { setFlash(msg); setTimeout(() => setFlash(null), 2800) }

  useEffect(() => {
    if (!token) { setLoading(false); return }
    Promise.all([
      api.producer.getMarches().catch(() => []),
      api.producer.getHistory().catch(() => []),
      // MVP (S62) : sans Paniers, pas d'appel tournees ni de hero « Prochaine tache »
      MVP.PANIERS ? api.producer.getTours().catch(() => []) : Promise.resolve([]),
    ])
      .then(([m, h, t]) => {
        setMarches(Array.isArray(m) ? m : [])
        setHistory(Array.isArray(h) ? h : [])
        setTours(Array.isArray(t) ? t : [])
      })
      .finally(() => setLoading(false))
  }, [token])

  const prochain = marches
    .filter(p => p.marche && isFutureOrToday(p.marche.date))
    .sort((a, b) => new Date(a.marche.date) - new Date(b.marche.date))[0]


  // Prochaine tache (v7.3) : premiere tournee active ou une action urgente est
  // attendue, sinon la plus proche — le hero l'affiche et ouvre le module.
  const actives = trierTournees(tours)
  const tournee = actives.find((t) => tacheDe(t).urgent) || actives[0] || null
  const tache = tournee ? tacheDe(tournee) : null

  // CA du mois courant + mini-graphe des 6 derniers mois (donnees reelles)
  const now = new Date()
  const caParMois = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1)
    return history
      .filter(p => p.ca != null && p.marche?.date)
      .filter(p => { const md = new Date(p.marche.date); return md.getMonth() === d.getMonth() && md.getFullYear() === d.getFullYear() })
      .reduce((s, p) => s + Number(p.ca), 0)
  })
  const caMois = caParMois[5]
  const maxCa = Math.max(...caParMois, 1)

  return (
    <>
      {flash && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg animate-fade-up text-white bg-green">
          {flash}
        </div>
      )}
      {(
        <RinaContextCard
         
          message="Votre prochain évènement et votre stand, en un coup d'œil."
        />
      )}

      {/* ===== HERO (glassDark, barre d'accent module + halo — proto).
          v7.3 : une tournee active → carte « Prochaine tache » (module Paniers) ;
          sinon hero marche/offres v7.2 en secours. Pendant le chargement :
          squelettes, puis TOUTE la page monte d'un bloc pour que la cascade
          animate-fade-up joue comme au prototype (pas de swap carte par carte). ===== */}
      {loading ? (
        <>
          <div className="glass-dark rounded-[26px] animate-pulse" style={{ height: 178 }} />
          <div className={`rounded-3xl mt-3 animate-pulse ${cardBg}`} style={{ height: 120 }} />
        </>
      ) : (
        <>
        {tournee ? (
        <button
          onClick={onOpenPaniersProd}
          className="glass-dark relative w-full text-left rounded-[26px] overflow-hidden animate-fade-up"
          style={{ color: 'rgb(var(--c-creme))', padding: '18px 18px 16px' }}
        >
          <span className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${avecAlpha(MOD.paniers.c, 0.4)}, transparent 70%)` }} />
          <span className="relative flex items-center justify-between gap-2">
            <span className="text-[10.5px] font-bold uppercase tracking-[0.2em] truncate" style={{ color: 'rgb(var(--c-creme) / 0.7)' }}>
              Prochaine tâche · {tournee.nom || `Tournée du ${fmtDate(tournee.dateLivraison)}`}
            </span>
            <ModuleChip m={MOD.paniers} />
          </span>
          <span className="relative block font-serif text-[26px] leading-tight mt-2">
            {TITRE_TACHE[tournee.statut] || 'Votre tournée'}
          </span>
          <span className="relative block text-[12.5px] mt-1" style={{ color: 'rgb(var(--c-creme) / 0.75)' }}>
            {tache.urgent ? '⚠️ ' : ''}{tache.txt}
          </span>
          <span className="relative mt-3.5 block w-full text-center py-3 rounded-full font-extrabold text-[13.5px] text-green"
            style={{ background: MOD.paniers.c }}>
            Voir la tournée 🧺
          </span>
        </button>
        ) : null}

      {/* ===== Évènements (S62) : le prochain en avant + les suivants, memes
          cartes condensees que l'onglet Évènements (une seule source) —
          remplace l'ancien hero sombre et la carte « Vous etes inscrit ». ===== */}
      <div className="mt-1">
        <MarcheProducerEvents enAvant limite={3} titre={null} onToast={toast} onVoirTout={onOpenMarches} />
      </div>

      {/* ===== Dossier exposant (v7.5) : seulement « en verification » ===== */}
      <CarteDossierAccueil />

      {/* ===== Carte « Ma page » : jauge de completude (S62, vision MVP) ===== */}
      <CarteMaPage />

      {/* ===== Carte « Votre activite · {mois} » (proto : CA + mini-graphe → Detail) —
          masquee hors MVP.PRODUCTEUR_ACTIVITE (S62) ===== */}
      {MVP.PRODUCTEUR_ACTIVITE && (
      <button
        onClick={() => onTabChange?.('activite')}
        className={`w-full text-left rounded-3xl p-4 mt-3 animate-fade-up ${cardBg}`}
        style={{ animationDelay: '0.08s' }}
      >
        <div className="flex justify-between items-start">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em]" style={{ color: 'rgb(var(--c-teal))' }}>
              Votre activité · {MOIS[now.getMonth()]}
            </p>
            <p className={`font-serif text-[30px] leading-none mt-1.5 ${tc}`}>{`${eur(caMois)} €`}</p>
          </div>
          <span className={`text-[11.5px] font-bold px-3 py-1.5 rounded-full ${tc}`}
            style={{ background: 'rgb(var(--c-teal) / 0.15)' }}>Détail ›</span>
        </div>
        <div className="flex items-end gap-1.5 mt-3" style={{ height: 34 }}>
          {caParMois.map((v, i) => (
            <span key={i} className="flex-1 rounded-[5px]"
              style={{ height: `${Math.max(6, (v / maxCa) * 34)}px`, background: i === 5 ? 'rgb(var(--c-primary))' : 'rgb(var(--c-teal) / 0.3)' }} />
          ))}
        </div>
      </button>
      )}

      {/* ===== Feed « En ce moment » (proto : bord gauche colore + chip module) —
          masque en MVP (S62) : Offres est deja dans la barre du bas ===== */}
      {MVP.ACCUEIL_PRODUCTEUR_FEED && (
      <>
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] mt-5 mb-2.5 px-0.5" style={{ color: 'rgb(var(--c-teal))' }}>
        En ce moment
      </p>
      <div className="flex flex-col gap-2.5">
        {[
          { m: MOD.offres, e: '📦', t: 'Vos offres, un seul stock', x: 'Activez ou coupez un canal de vente en un geste.', on: onOpenOffers },
          // MVP (S62) : les versements (Stripe Connect) suivent MVP.PRODUCTEUR_PAIEMENTS
          ...(MVP.PRODUCTEUR_PAIEMENTS ? [{ m: MOD.paniers, e: '💶', t: 'Vos versements', x: 'Suivez vos paiements en ligne et vos commissions.', on: () => onTabChange?.('paiement') }] : []),
        ].map((f, i) => (
          <button
            key={f.t}
            onClick={f.on}
            style={{ animationDelay: `${0.16 + i * 0.06}s` }}
            className={`flex items-center gap-3 text-left rounded-[20px] p-3.5 animate-fade-up active:scale-[0.99] transition-transform ${cardBg}`}
          >
            <span className="w-11 h-11 rounded-[14px] flex items-center justify-center text-[22px] flex-shrink-0"
              style={{ background: avecAlpha(f.m.c, 0.13), border: `1px solid ${avecAlpha(f.m.c, 0.23)}` }}>{f.e}</span>
            <span className="flex-1 min-w-0">
              <span className="block mb-0.5"><ModuleChip m={f.m} /></span>
              <span className={`block text-[13.5px] font-bold ${tc}`}>{f.t}</span>
              <span className={`block text-[11.5px] mt-0.5 leading-snug ${ts}`}>{f.x}</span>
            </span>
          </button>
        ))}
      </div>
      </>
      )}
        </>
      )}
    </>
  )
}

// La page porte SON layout (S51) : retour et barre declares ici, la route
// d'App.jsx ne fait que router.
export default function PageAccueilProducteur() {
  return <PageHub><ContenuAccueilProducteur /></PageHub>
}

